import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { config } from '../../config';
import { fetchWithCSRF } from '../../utils/apiUtils';

// Asynchronous thunk to fetch card by ID
export const fetchCardById = createAsyncThunk(
    'card/fetchCardById',
    async (cardId, { rejectWithValue }) => {
        try {
            const response = await fetchWithCSRF(`${config.API_URI}/v1/cards/${cardId}`, {
                credentials: 'include',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                const errorMessage = errorData.message || 'Failed to fetch card';

                return rejectWithValue({
                    status: response.status,
                    message: errorMessage,
                    type: response.status === 403 ? 'ACCESS_DENIED' :
                          response.status === 404 ? 'NOT_FOUND' : 'ERROR'
                });
            }

            const data = await response.json();
            return data;
        } catch (error) {
            return rejectWithValue({
                status: 500,
                message: error.message || 'Failed to fetch card',
                type: 'ERROR'
            });
        }
    }
);

// Asynchronous thunk to update card
export const updateCard = createAsyncThunk(
    'card/updateCard',
    async ({ cardId, updatedData }, { getState }) => {



        const response = await fetchWithCSRF(`${config.API_URI}/v1/cards/update/${cardId}`, {
            method: 'POST',
            credentials: 'include',
            headers: {

                'Content-Type': 'application/json'
            },
            body: JSON.stringify(updatedData)
        });

        if (!response.ok) {
            throw new Error('Failed to update card');
        }

        const data = await response.json();
        return data;  // Returns the updated card
    }
);

// Asynchronous thunk to fetch archived data
export const fetchArchivedData = createAsyncThunk(
    'card/fetchArchivedData',
    async ({ boardId, activeTab, page, debouncedQuery }) => {
        const response = await fetchWithCSRF(`${config.API_URI}/v1/${boardId}/${activeTab}/archived?page=${page}&limit=20&search=${debouncedQuery}`, {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        });
        const data = await response.json();


        return { ...data, activeTab };
    }
);

// Asynchronous thunk to handle archiving a card
export const handleArchiveCard = createAsyncThunk(
    'card/handleArchiveCard',
    async ({ archivedStatus, cardId }, { getState }) => {


        const response = await fetchWithCSRF(config.API_URI + `/v1/cards/archive/${cardId}`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',

            },
            body: JSON.stringify({ archived: archivedStatus }),
        });

        if (!response.ok) {
            throw new Error('Failed to archive the card');
        }

        const data = await response.json();
        return data;
    }
);

// Asynchronous thunk to handle archiving a list
export const handleArchiveList = createAsyncThunk(
    'card/handleArchiveList',
    async ({ boardId, listId, archivedStatus }) => {
        const response = await fetchWithCSRF(config.API_URI + `/v1/actionLists/archive/${listId}`, {
            method: 'PUT',
            credentials: 'include',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ boardId, archivedStatus }),
        });

        if (!response.ok) {
            throw new Error('Failed to update the list');
        }

        const data = await response.json();
        return data; // Returns { actionList }
    }
);

const cardSlice = createSlice({
    name: 'card',
    initialState: {
        card: null,
        archivedCards: [],
        archivedLists: [],
        cardsCount: 0,
        listsCount: 0,
        hasMore: true,
        activeTab: 'cards',
        status: 'idle',
        error: null,
        optimisticUpdates: {}
    },
    reducers: {
        cardData: (state, action) => {
            state.card = action.payload;
        },
        resetArchivedCards: (state) => {
            state.archivedCards = [];
            state.archivedLists = [];
            state.cardsCount = 0;
            state.hasMore = true;
        },
        removeArchivedCard: (state, action) => {
            state.archivedCards = state.archivedCards.filter(card => card.shortId !== action.payload);
            state.cardsCount = state.cardsCount - 1;
        },
        removeArchivedList: (state, action) => {
            state.archivedLists = state.archivedLists.filter(archivedList => archivedList._id !== action.payload);
            state.listsCount = state.listsCount - 1;
        },
        pushArchivedCard: (state, action) => {
            const card = action.payload;
            // Prevent duplicates
            if (!state.archivedCards.some(c => c._id === card._id)) {
                state.archivedCards = [card, ...state.archivedCards];
                state.cardsCount += 1;
            }
        },
        pushArchivedList: (state, action) => {
            const list = action.payload;
            // Prevent duplicates
            if (!state.archivedLists.some(l => l._id === list._id)) {
                state.archivedLists = [list, ...state.archivedLists];
                state.listsCount += 1;
            }
        },
        updatePriorityOptimistically: (state, action) => {
            const { cardId, priority } = action.payload;
            if (state.card && state.card.shortId === cardId) {
                // Store the previous priority for potential rollback
                state.optimisticUpdates[cardId] = state.card.priority;
                // Update the card priority optimistically
                state.card.priority = priority;
            }
        },
        rollbackPriorityUpdate: (state, action) => {
            const { cardId } = action.payload;
            if (state.card && state.card.shortId === cardId && state.optimisticUpdates[cardId]) {
                // Restore the previous priority
                state.card.priority = state.optimisticUpdates[cardId];
                // Remove the stored update
                delete state.optimisticUpdates[cardId];
            }
        },

        // ========== OPTIMISTIC ACTIONS FOR REAL-TIME UPDATES ==========

        // Update current card (for CardDetails view)
        optimisticUpdateCurrentCard: (state, action) => {
            if (state.card) {
                state.card = { ...state.card, ...action.payload };
            }
        },

        // Update card archived status
        optimisticArchiveCard: (state, action) => {
            const { cardId, archived } = action.payload;
            if (state.card && (state.card._id === cardId || state.card.shortId === cardId)) {
                state.card.archived = archived;
            }
        },

        // Toggle activity details
        toggleActivityDetails: (state, action) => {
            if (state.card) {
                state.card.isActivityDetails = action.payload;
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCardById.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchCardById.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.card = action.payload || null; // Ensure card is set to null if action.payload is empty
            })
            .addCase(fetchCardById.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            })

            // Handling the updateCard cases
            .addCase(updateCard.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(updateCard.fulfilled, (state, action) => {
                state.status = 'succeeded';
                // Ensure that action.payload contains a valid card object before assigning
                state.card = action.payload ? action.payload : state.card; // Update the card only if payload is valid
            })
            .addCase(updateCard.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            })

            // Handling the fetchArchivedData cases
            .addCase(fetchArchivedData.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchArchivedData.fulfilled, (state, action) => {
                state.status = 'succeeded';

                if (action.meta.arg.page === 1) {
                    if (action.payload.activeTab === 'cards') {
                        // Merge logic: prefer server data but KEEP any cards that are archived:true in local state 
                        // but not yet in server results (optimistic additions)
                        const serverCards = action.payload.cards || [];
                        const localOptimisticCards = state.archivedCards.filter(
                            localCard => !serverCards.some(serverCard => serverCard._id === localCard._id)
                        );
                        
                        state.archivedCards = [...localOptimisticCards, ...serverCards];
                        state.cardsCount = action.payload.cardsCount + localOptimisticCards.length;
                    } else {
                        // Merge logic for lists (optimistic additions)
                        const serverLists = action.payload.lists || [];
                        const localOptimisticLists = state.archivedLists.filter(
                            localList => !serverLists.some(serverList => serverList._id === localList._id)
                        );
                        
                        state.archivedLists = [...localOptimisticLists, ...serverLists];
                        state.listsCount = action.payload.listsCount + localOptimisticLists.length;
                    }
                } else {
                    if (action.payload.activeTab === 'cards') {
                        const uniqueCards = new Map([
                            ...state.archivedCards.map(card => [card._id, card]),
                            ...action.payload.cards.map(card => [card._id, card])
                        ]);
                        state.archivedCards = Array.from(uniqueCards.values());
                    } else {
                        state.archivedLists.push(...action.payload.lists);
                        state.listsCount = action.payload.listsCount;
                    }
                }

                state.hasMore = action.payload.hasMore;
            })
            .addCase(fetchArchivedData.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            })
            // Handling the handleArchiveCard cases
            .addCase(handleArchiveCard.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(handleArchiveCard.fulfilled, (state, action) => {
                state.status = 'succeeded';
                const updatedCard = action.payload.updatedCard;
                
                if (updatedCard.archived) {
                    // If it was archived, ensure it's in the list (or update if already there)
                    const index = state.archivedCards.findIndex(c => c._id === updatedCard._id);
                    if (index !== -1) {
                        state.archivedCards[index] = updatedCard;
                    } else {
                        state.archivedCards = [updatedCard, ...state.archivedCards];
                        state.cardsCount += 1;
                    }
                } else {
                    // If it was unarchived, remove it from the list
                    state.archivedCards = state.archivedCards.filter(card => card._id !== updatedCard._id);
                    state.cardsCount = Math.max(0, state.cardsCount - 1);
                }

                // Update the single card's archived status if it's being viewed
                if (state.card && state.card._id === updatedCard._id) {
                    state.card.archived = updatedCard.archived;
                }
            })
            .addCase(handleArchiveCard.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            })
            // Handling handleArchiveList cases
            .addCase(handleArchiveList.fulfilled, (state, action) => {
                state.status = 'succeeded';
                const { actionList } = action.payload;
                
                if (actionList.archived) {
                    // Update or adding to the list
                    const index = state.archivedLists.findIndex(l => l._id === actionList._id);
                    if (index !== -1) {
                        state.archivedLists[index] = actionList;
                    } else {
                        state.archivedLists = [actionList, ...state.archivedLists];
                        state.listsCount += 1;
                    }
                } else {
                    // Remove from the list
                    state.archivedLists = state.archivedLists.filter(l => l._id !== actionList._id);
                    state.listsCount = Math.max(0, state.listsCount - 1);
                }
            })
            .addCase(handleArchiveList.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            });
    }
});

export const {
    cardData,
    resetArchivedCards,
    removeArchivedCard,
    removeArchivedList,
    updatePriorityOptimistically,
    rollbackPriorityUpdate,
    // Optimistic actions
    optimisticUpdateCurrentCard,
    optimisticArchiveCard,
    pushArchivedCard,
    pushArchivedList,
    toggleActivityDetails,
} = cardSlice.actions;
export default cardSlice.reducer;